import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/slw/shared/lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center rounded-lg text-sm font-medium',
    'transition-colors focus-visible:outline-none',
    'disabled:opacity-50 disabled:pointer-events-none',
  ].join(' '),
  {
    variants: {
      variant: {
        default: 'bg-[#C9AA71] text-white hover:bg-[#B89961]',
        outline: 'border border-input hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary hover:text-primary/80',
      },
      size: {
        default: 'h-12 py-2 px-4',
        sm: 'h-9 px-3',
        lg: 'h-13.25 p-4',
        icon: 'h-10 w-10',
      },
      color: {
        default: 'bg-[#C9AA71] text-white hover:bg-[#B89961]',
        primary: 'bg-[#C9AA71] text-white hover:bg-[#B89961]',
        secondary: 'border border-input hover:bg-accent hover:text-accent-foreground',
        success: 'hover:bg-accent hover:text-accent-foreground',
        light: 'bg-white text-black hover:bg-accent hover:text-accent-foreground',
      },
    },

    compoundVariants: [
      // default
      {
        variant: 'default',
        color: 'primary',
        className: 'bg-[#C9AA71] text-white hover:bg-[#B89961]',
      },
      {
        variant: 'default',
        color: 'secondary',
        className: 'border border-input hover:bg-accent hover:text-accent-foreground',
      },
      {
        variant: 'default',
        color: 'success',
        className: 'hover:bg-accent hover:text-accent-foreground',
      },
      // outline
      {
        variant: 'outline',
        color: 'primary',
        className: 'bg-white border border-primary text-primary hover:bg-primary/10 hover:text-primary',
      },
      {
        variant: 'outline',
        color: 'secondary',
        className: 'border border-input hover:bg-accent hover:text-accent-foreground',
      },
      {
        variant: 'outline',
        color: 'success',
        className: 'hover:bg-accent hover:text-accent-foreground',
      },
      // ghost
      {
        variant: 'ghost',
        color: 'primary',
        className: 'bg-transparent text-primary hover:bg-transparent hover:text-primary',
      },
      {
        variant: 'ghost',
        color: 'secondary',
        className: 'hover:bg-accent hover:text-accent-foreground',
      },
      {
        variant: 'ghost',
        color: 'success',
        className: 'hover:bg-accent hover:text-accent-foreground',
      },
      // link
      {
        variant: 'link',
        color: 'primary',
        className: 'text-primary hover:text-primary/80',
      },
      {
        variant: 'link',
        color: 'secondary',
        className: 'text-secondary hover:text-secondary/80',
      },
      {
        variant: 'link',
        color: 'success',
        className: 'text-success hover:text-success/80',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      color: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  color?: 'default' | 'primary' | 'secondary' | 'success' | 'light';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, color, ...props }, ref) => {
    return (
      <button type='button' className={cn(buttonVariants({ variant, size, color, className }))} ref={ref} {...props} />
    );
  },
);

Button.displayName = 'Button';

export { Button, buttonVariants };
